import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Stepper, Step, StepLabel, useMediaQuery } from "@mui/material";
import {
  MButton,
  MHeader,
  BufferService,
  LoadingContext,
  NavigateContext,
  M_StateActions,
  MTextArea,
} from "src/M_Components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";

// Sub-forms
import DynamicForm from "../../components/Forms/DynamicForm";
import DynamicSelectBox from "../../components/Forms/DynamicSelectBox";
import SelectableTileGrid from "../../components/SelectableTileGrid/SelectableTileGrid";
import TenderCarrierDetailView from "../Tenders/TenderCarrierDetailView";
import RoutePreviewTable from "../../components/routePreview/routePreviewTable";
import PackagesPanel from "../../components/InstantQuotesSearch/PackagesPanel";

import "./createTenders.css";
//==================================================================================================================================================================
// Builds a 4-step wizard to create a Tender (Spot or Non-Spot) or create a Phase for a Non-Spot Tender.
// - Per-tab persistence via Redux KV (isolated namespace per tab)
// - Preloads lookups and options (Tenders/Phases) with i18n support
// - Shipper-dependent markets (no fallback to all markets)
// - Carriers: fetch ONLY invitable carriers from API on step 3 (no client-side filtering over all carriers)
//==================================================================================================================================================================
export default function CreateTenderPage({ toastMessages, handleTabClose }) {
  const { t, i18n } = useTranslation();
  const steps = ["MenuCreateTendersLabel", "Lanes", "Carriers", "Message"];
  const matches = useMediaQuery("(max-width: 570px)");
  const matchesAlternative = useMediaQuery("(max-width: 1030px)");
  const [activeStep, setActiveStep] = useState(0);

  // Contexts and redux
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const M_State = useSelector((s) => s.StateKeyValue);

  const baseUrl = ctx.DEFAULTURL;
  const QF_ACTIVE = encodeURIComponent("Inactive eq false");

  // --- PAGE GUARD: disable whole page if we cannot resolve a default shipper ---
  const [pageDisabled, setPageDisabled] = useState(false);

  // Small helper to push a translated/bilingual toast consistently
  const notifyDefaultShipperFailure = () => {
    // Bilingual message as requested
    toastRef.current("error", "", t("FailDetectDefaultShipper"));
  };

  // Router seeds
  const location = useLocation();
  const {
    isCreatePhase = false,
    tenderInfo = null,
    lastPhase = null,
    phases = [],
    quotes = [],
    lanes = [],
    carriers = [],
  } = location.state || {};

  // API services
  const svcTenderCreate = useMemo(
    () => new BufferService("Tenders/create", baseUrl),
    [baseUrl]
  );
  const svcTenderUpdate = useMemo(
    () => new BufferService("Tenders/update", baseUrl),
    [baseUrl]
  );
  const svcPhaseCreate = useMemo(
    () => new BufferService("TenderPhases/create", baseUrl),
    [baseUrl]
  );
  const svcTransportTypes = useMemo(
    () => new BufferService("TransportTypes", baseUrl),
    [baseUrl]
  );
  const svcPackageTypes = useMemo(
    () => new BufferService("PackageTypes", baseUrl),
    [baseUrl]
  );
  const svcTenderTypes = useMemo(
    () => new BufferService("TenderTypes", baseUrl),
    [baseUrl]
  );
  const svcRoutes = useMemo(() => new BufferService("Routes", baseUrl), [baseUrl]);
  const svcMarkets = useMemo(
    () => new BufferService("Markets", baseUrl),
    [baseUrl]
  );
  const svcTenderPhases = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );
  const svcTenderOptions = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );
  const svcTenderGet = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  ); // Tender DTO

  // Invitable carriers endpoint (POST)
  // NOTE: BufferService base path already uses baseUrl; endpoint here is relative ("Tenders/InvitableCarriers")
  const svcInvitableCarriers = useMemo(
    () => new BufferService("Tenders/InvitableCarriers", baseUrl),
    [baseUrl]
  );

  // Lookups
  const [packageTypes, setPackageTypes] = useState([]);
  const [tenderTypeOptions, setTenderTypeOptions] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);

  // Shipper-dependent markets (for create tender mode)
  const [shipperMarkets, setShipperMarkets] = useState([]);

  // PHASE: schema/FKs/selects
  const [phaseSchemaOptions, setPhaseSchemaOptions] = useState([]);
  // PHASE-only (inherited from parent tender)
  const [phaseMarkets, setPhaseMarkets] = useState([]);
  const [phaseTransportTypes, setPhaseTransportTypes] = useState([]);
  const [dtoPhases, setDtoPhases] = useState([]);

  // TENDER: schema/FKs
  const [tenderSchemaOptions, setTenderSchemaOptions] = useState([]);

  // Grid states by tab (namespaced)
  const [mkGridState, setMkGridState] = useState(null); // Markets
  const [ttGridState, setTtGridState] = useState(null); // TransportTypes
  const [crGridState, setCrGridState] = useState(null); // Carriers

  // Wizard state (flat Tender)
  const [tenderData, setTenderData] = useState({
    // UI/state uses PascalCase: TenderTypeId; payload maps to camelCase
    TenderTypeId: isCreatePhase ? 2 : 1, // 1=Spot (default), 2=Non-Spot (when creating phase)
    routeId: null,
    message: "",
    ShipperId: null,
    ShipmentStartDate: null,
    ShipmentDeadline: null,
    ResponseDeadline: null,
    Name: "",
    PickupAddress: "",
    DeliveryAddress: "",
    DeliverTo: "",
    MaxCarriers: null,
    MaxCarriersByLane: null,
    MaxCarriersInOrigin: null,
    MaxCarriersInDestination: null,
    marketIds: [],
    transportTypeIds: [],
    ContainerTypeId: 2, // default FCL (2). 1=LCL, 2=FCL
    fclItems: [],
    lclMode: "volumes",
    lclBase: "plt",
    lclvolumes: { quantity: "", volumeM3: "", weightKg: "" },
    lclDimensions: [],
    carrierIds: [],
    tenderPhase: undefined,
  });

  // POST guard (prevents double submit)
  const [isPosting, setIsPosting] = useState(false);
  const isPostingRef = useRef(false);


  // Initial page loading gate:
  // - Keeps the global loading overlay ON until essential data is loaded once.
  // - Prevents the UI from visually "building/growing" while async effects complete.
  const [lookupsLoadedOnce, setLookupsLoadedOnce] = useState(false);
  const [tenderSchemaLoadedOnce, setTenderSchemaLoadedOnce] = useState(false);
  const [phaseSchemaLoadedOnce, setPhaseSchemaLoadedOnce] = useState(false);
  const initialGateReleasedRef = useRef(false);

  // Per-tab persistence
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );
  const tabsList = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === "tabs")?.value
  );
  const stateKeyFrom = (tid) => (tid ? `${tid}::CT` : null); // Namespace key builder
  const K = useMemo(() => ({
    owner: "CT.ownerTabId",
    step: "CT.step",
    data: "CT.data",
    first: "CT.isFirst",
    gridMk: "CT.grid.markets",
    gridTt: "CT.grid.transportTypes",
    gridCr: "CT.grid.carriers",
    carriersSel: "CT.carriers.selected",
    isCreatePhase: "CT.seed.isCreatePhase",
    tenderInfo: "CT.seed.tenderInfo",
    lastPhase: "CT.seed.lastPhase",
    phases: "CT.seed.phases",
    quotes: "CT.seed.quotes",
    lanes: "CT.seed.lanes",
    carriers: "CT.seed.carriers",
    phaseMkts: "CT.phaseMarkets",
    phaseTTs: "CT.phaseTransportTypes",
    shipperMkts: "CT.shipperMarkets",
  }), []);
  const pick = useCallback((arr, k) => (arr || []).find((x) => x.key === k)?.value, []); // Picks a value from session array

  const [hydrated, setHydrated] = useState(false);
  const [hydrateKey, setHydrateKey] = useState(0);
  const [isFirstTime, setIsFirstTime] = useState(true);

  const activeNsRef = useRef(null);
  const activeTidRef = useRef(null);
  const prevTabRef = useRef(null);
  const stepRef = useRef(0); // 0-based
  const dataRef = useRef(tenderData);

  // Debounced persistence
  const persistDebounceRef = useRef(null);
  const lastDraftJsonRef = useRef("");

  // Stable refs for t and toastMessages
  const tRef = useRef(t);
  const toastRef = useRef(toastMessages);
  const gridDebounceRef = useRef({ mk: null, tt: null, cr: null });

  // Available carriers (server-filtered), plus selected carrier ids
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [linkedCarriers, setLinkedCarriers] = useState([]);

  //================================================================================================================================
  // Paged loader for TransportTypes (Inactive == false), cautious and idempotent ---
  // No need to fetch a big payload all at once; also works around API filtering limitations
  // We don't expect a huge number of transport types, but this is future-proof and stable
  //Starts fetching a relatively small page size
  // Only fetches more if there are more pages indicated by the API response
  //==================================================================================================================================
  const fetchAllTransportTypesPaged = async () => {
    const pageSize = 10;
    let page = 1;
    const acc = [];
    const seen = new Set();
    // keep going while the API says there are more items
    while (true) {
      const res = await svcTransportTypes.filter(
        `?Page=${page}&PageSize=${pageSize}&filter=${QF_ACTIVE}`
      );
      if (isFailurePayload(res)) break;

      const items = Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res)
          ? res
          : [];
      if (!items?.length) break;

      for (const it of items) {
        const id = it?.Id;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          acc.push(it);
        }
      }

      const totalPages = Number(res?.TotalPages ?? 1);
      if (page >= totalPages) break;
      page += 1;
    }

    // sort for stable UX
    acc.sort((a, b) =>
      String(a?.Name ?? "").localeCompare(String(b?.Name ?? ""))
    );
    return acc;
  };

  //================================================================================================================================
  // Whitelist of selectable Market IDs based on mode (create tender vs create phase), based on preloaded shipper or phase markets
  // ===============================================================================================================================
  const selectableMarketIds = useMemo(
    () =>
      isCreatePhase
        ? (phaseMarkets || []).map((m) => m.Id)
        : Array.isArray(shipperMarkets)
          ? shipperMarkets.map((m) => m.Id)
          : null, //
    [isCreatePhase, phaseMarkets, shipperMarkets]
  );

  //================================================================================================================================
  // Whitelist of selectable Carrier IDs (server already filtered them; we still use IDs for fast O(1) checks)
  //================================================================================================================================
  const selectableCarrierIds = useMemo(() => {
    const list = Array.isArray(availableCarriers) ? availableCarriers : [];
    return list.map((c) => c.Id ?? c.CarrierId ?? c.carrierId).filter((v) => v != null);
  }, [availableCarriers]);

  //===========================================================================================================================
  // Add a tab-scoped prefix to IDs for form field keys and similar
  //===========================================================================================================================
  const [scopeBump, setScopeBump] = useState(0);
  const tabScopeId = useMemo(() => {
    const tid = activeTidRef.current ?? selectedTab;
    return `ct-${String(tid ?? "unknown")}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeBump]); // bump when tab changes, not on every render

  //===========================================================================================================================
  // ----- includeInCreateTender -----
  // Override the DynamicForm inclusion logic for TENDER form
  //===========================================================================================================================
  const includeInCreateTender = useCallback((opt) => {
    // mirrors TenderMainForm override
    if (!opt) return false;
    const name = opt.Name;
    const isAudit = ["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(
      name
    );
    if (opt.IsKey || isAudit) return false;
    if (opt.IsHiddenCreate === true) return false;
    if (opt.IsHiddenEdit === true) return false; // legacy fallback
    if (name === "IsFinished" || name === "Inactive" || name === "Message")
      return false;
    return true;
  }, []);

  //===========================================================================================================================
  // ----- includeInCreatePhase -----
  // Override the DynamicForm inclusion logic for PHASE form
  //===========================================================================================================================
  const includeInCreatePhase = useCallback((opt) => {
    // mirrors TenderPhaseForm override
    if (!opt) return false;
    const name = opt.Name;
    const isAudit = ["CreatedAt", "UpdatedAt", "CreatedBy", "UpdatedBy"].includes(
      name
    );
    if (opt.IsKey || isAudit) return false;
    if (opt.IsHiddenCreate === true) return false;
    if (opt.IsHiddenEdit === true) return false; // legacy compatibility
    if (name === "IsFinished" || name === "Inactive") return false;
    return true;
  }, []);

  useEffect(() => {
    tRef.current = t;
  }, [t]); // keep translation ref fresh
  useEffect(() => {
    toastRef.current = toastMessages;
  }, [toastMessages]); // keep toast ref fresh

  // Loading counter bound to ctx.setIsLoad (stable ref to avoid stale closures across tab/router re-mounts)
  const loadCountRef = useRef(0);
  const setLoadRef = useRef(ctx.setIsLoad);

  useEffect(() => {
    // Keep a stable reference to the loading toggle function from context
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  const incLoad = useCallback(() => {
    const prev = loadCountRef.current;
    loadCountRef.current = prev + 1;

    // Only turn on when crossing 0 -> 1
    if (prev === 0) setLoadRef.current?.(true);
  }, []);

  const decLoad = useCallback(() => {
    const prev = loadCountRef.current;
    loadCountRef.current = Math.max(0, prev - 1);

    // Only turn off when crossing 1 -> 0
    if (prev === 1) setLoadRef.current?.(false);
  }, []);


  // --------------------------------------------------------------------------------------------------
  // Initial global loading overlay: ON until the page is ready.
  // "Ready" means essential initial loads finished once and the per-tab snapshot is hydrated.
  // --------------------------------------------------------------------------------------------------
  useEffect(() => {
    // Engage the gate on mount. This uses the same counter as other loads,
    // so parallel async operations do not flicker the overlay.
    incLoad();
    return () => {
      // If the component unmounts before the gate is released, ensure we clean up the counter.
      if (!initialGateReleasedRef.current) decLoad();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ready =
      hydrated && lookupsLoadedOnce && tenderSchemaLoadedOnce && phaseSchemaLoadedOnce;

    if (ready && !initialGateReleasedRef.current) {
      initialGateReleasedRef.current = true;
      decLoad(); // releases the initial gate (overlay can still remain ON if other loads are in flight)
    }
  }, [hydrated, lookupsLoadedOnce, tenderSchemaLoadedOnce, phaseSchemaLoadedOnce, decLoad]);

  //==================================================================================================================================================================
  // Deep equality via JSON stringification with try/catch fallback
  //==================================================================================================================================================================
  const deepEq = useCallback((a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  }, []);

  //==================================================================================================================================================================
  // Conditionally persists a set of key/values into the per-tab session namespace
  //==================================================================================================================================================================
  const setSession = useCallback((nsKey, entries) => {
    if (!nsKey) return; // guard: no namespace
    const current = M_State.find((o) => o.key === nsKey)?.value || []; // existing snapshot
    const map = new Map(current.map((e) => [e.key, e])); // index by key
    let changed = false; // track if any change
    for (const kv of entries) {
      const prevVal = map.get(kv.key)?.value; // previous value
      if (!deepEq(prevVal, kv.value)) {
        // only persist on actual change
        changed = true;
        map.set(kv.key, kv);
      }
    }
    if (changed) {
      dispatch(
        M_StateActions.addM_State({
          key: nsKey,
          value: Array.from(map.values()),
        })
      ); // write snapshot
    }
  }, [M_State, dispatch, deepEq]);

  //==================================================================================================================================================================
  // Debounced grid state persistence
  //==================================================================================================================================================================
  const persistGridState = useCallback(
    (ns, key, val, tag) => {
      if (!ns) return;
      const dref = gridDebounceRef.current;
      if (dref[tag]) clearTimeout(dref[tag]);
      dref[tag] = setTimeout(() => {
        // persist after debounce
        const snap = M_State.find((o) => o.key === ns)?.value || [];
        const prev = pick(snap, key);
        if (deepEq(prev, val)) return; // no change
        setSession(ns, [{ key, value: val }]);
        dref[tag] = null;
      }, 250);
    },
    [M_State, setSession, pick, deepEq]
  );

  //==================================================================================================================================================================
  // Flush any pending grid state debounces immediately
  //==================================================================================================================================================================
  const flushGridDebounces = useCallback(() => {
    const dref = gridDebounceRef.current;
    if (!dref) return;
    const ns = activeNsRef.current;
    if (!ns) return;

    // Cancel and flush each pending debounce
    if (dref.mk) {
      clearTimeout(dref.mk);
      dref.mk = null;
      setSession(ns, [{ key: K.gridMk, value: mkGridState }]);
    }
    if (dref.tt) {
      clearTimeout(dref.tt);
      dref.tt = null;
      setSession(ns, [{ key: K.gridTt, value: ttGridState }]);
    }
    if (dref.cr) {
      clearTimeout(dref.cr);
      dref.cr = null;
      setSession(ns, [{ key: K.gridCr, value: crGridState }]);
    }
  }, [mkGridState, ttGridState, crGridState, setSession, K]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Rename tab when locale or selection changes
  //==================================================================================================================================================================
  useEffect(() => {
    if (!selectedTab || !renameTab) return; // guard
    renameTab(String(selectedTab), tRef.current("MenuCreateTendersLabel")); // set tab label
  }, [selectedTab, i18n.language, renameTab]);

  useEffect(() => {
    if (isCreatePhase) return;
    const allowed = new Set((shipperMarkets || []).map((m) => m.Id));
    const prev = Array.isArray(tenderData.marketIds) ? tenderData.marketIds : [];
    const next = prev.filter((id) => allowed.has(id));
    if (next.length !== prev.length) onDraft({ marketIds: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((shipperMarkets || []).map((m) => m.Id)), isCreatePhase]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // GC orphaned per-tab namespaces when tabs list changes
  //==================================================================================================================================================================
  const didGCRef = useRef(false);

  useEffect(() => {
    if (didGCRef.current) return; // run once

    // 2) Fallback seguro dentro do efeito
    const safeTabsList = tabsList || [];
    if (safeTabsList.length === 0) return; // nothing to clean

    didGCRef.current = true; // mark executed

    try {
      const openIds = new Set(safeTabsList.map((t) => String(t.value))); // collect open tab ids

      (M_State || [])
        .map((x) => x.key)
        .filter((k) => typeof k === "string" && k.endsWith("::CT")) // only our namespaces
        .forEach((ns) => {
          const tid = ns.split("::")[0]; // tab id from namespace
          if (!openIds.has(String(tid))) {
            dispatch(M_StateActions.removeM_State({ key: ns })); // remove orphan namespace
          }
        });
    } catch {
      /* safe no-op */
    }
  }, [tabsList, M_State, dispatch]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Hydrate per-tab state on tab change and persist on tab switch
  //==================================================================================================================================================================
  useEffect(() => {
    const persistSnapshot = (forTabId = null) => {
      // Writes a minimal snapshot for the given (or current) tab id
      const tid = forTabId ?? activeTidRef.current ?? selectedTab; // resolve target tab id
      const ns = stateKeyFrom(tid); // resolve namespace key
      if (!ns) return; // guard

      const mk = mkGridState;
      const tt = ttGridState;
      const cr = crGridState;
      setSession(ns, [
        { key: K.owner, value: String(tid) },
        { key: K.step, value: stepRef.current },
        { key: K.data, value: dataRef.current },
        { key: K.first, value: false },
        { key: K.phaseMkts, value: phaseMarkets },
        { key: K.phaseTTs, value: phaseTransportTypes },
        { key: K.shipperMkts, value: shipperMarkets },
        { key: K.carriersSel, value: linkedCarriers },
        { key: K.gridMk, value: mk },
        { key: K.gridTt, value: tt },
        { key: K.gridCr, value: cr },
      ]);
    };

    const hydrateFromSession = (ns, tid) => {
      // Restores state for the given namespace and activates the tab-bound context
      const markActive = () => {
        activeNsRef.current = ns; // set active namespace
        activeTidRef.current = tid; // set active tab id
        lastDraftJsonRef.current = ""; // reset draft hash
        setScopeBump((k) => k + 1); // bump scope id to force re-keying where needed
        setHydrateKey((k) => k + 1); // bump key to force memoized children refresh where needed
      };

      const snap = M_State.find((o) => o.key === ns)?.value || []; // get snapshot array
      const owner = pick(snap, K.owner); // stored owner
      const snapData = pick(snap, K.data); // stored draft
      const snapStep = pick(snap, K.step); // stored step
      const snapFirst = pick(snap, K.first); // stored first flag
      const snapGridMk = pick(snap, K.gridMk);
      const snapGridTt = pick(snap, K.gridTt);
      const snapGridCr = pick(snap, K.gridCr);
      const snapCarSel = pick(snap, K.carriersSel);

      if (snapData || snapStep !== undefined || snapFirst === false) {
        // Snapshot exists: prefer it
        if (owner && String(owner) !== String(tid)) {
          // Owner mismatch: reset to a clean state for the new tab
          const reset = {
            TenderTypeId: isCreatePhase ? 2 : 1,
            routeId: null,
            message: "",
            ShipperId: null,
            ShipmentStartDate: null,
            ShipmentDeadline: null,
            ResponseDeadline: null,
            Name: "",
            PickupAddress: "",
            DeliveryAddress: "",
            DeliverTo: "",
            MaxCarriers: null,
            MaxCarriersByLane: null,
            MaxCarriersInOrigin: null,
            MaxCarriersInDestination: null,
            marketIds: [],
            transportTypeIds: [],
            ContainerTypeId: 2,
            fclItems: [],
            lclMode: "volumes",
            lclBase: "plt",
            lclvolumes: { quantity: "", volumeM3: "", weightKg: "" },
            lclDimensions: [],
            carrierIds: [],
            tenderPhase: undefined,
          };
          setTenderData(reset); // apply reset draft
          setActiveStep(0); // reset step
          dataRef.current = reset; // sync draft ref
          markActive(); // mark active context
          setScopeBump((k) => k + 1); // bump scope id to force re-keying where needed
          setHydrated(true); // hydration complete
          setSession(ns, [
            { key: K.owner, value: String(tid) },
            { key: K.isCreatePhase, value: !!isCreatePhase },
            { key: K.tenderInfo, value: tenderInfo },
            { key: K.lastPhase, value: lastPhase },
            { key: K.phases, value: phases },
            { key: K.quotes, value: quotes },
            { key: K.lanes, value: lanes },
            { key: K.carriers, value: carriers },
            { key: K.first, value: true },
            { key: K.step, value: 0 },
            { key: K.data, value: reset },
          ]);
          return;
        }
        setIsFirstTime(false); // not first time in this tab
        if (snapData) {
          setTenderData(snapData);
          dataRef.current = snapData;
        } // restore draft
        if (snapStep !== undefined) {
          setActiveStep(snapStep);
          stepRef.current = snapStep;
        } // restore step
        const pm = pick(snap, K.phaseMkts); // restore cached phase markets
        const tt = pick(snap, K.phaseTTs); // restore cached phase transport types
        const sm = pick(snap, K.shipperMkts); // restore cached shipper markets
        if (Array.isArray(pm)) setPhaseMarkets(pm);
        if (Array.isArray(tt)) setPhaseTransportTypes(tt);
        if (Array.isArray(sm)) setShipperMarkets(sm);
        if (snapGridMk) setMkGridState(snapGridMk);
        if (snapGridTt) setTtGridState(snapGridTt);
        if (snapGridCr) setCrGridState(snapGridCr);
        if (Array.isArray(snapCarSel)) setLinkedCarriers(snapCarSel);
        markActive();
        setHydrated(true);
        return;
      }

      // First time: initialize a clean state and seed store values
      const reset = {
        TenderTypeId: isCreatePhase ? 2 : 1,
        routeId: null,
        message: "",
        ShipperId: null,
        ShipmentStartDate: null,
        ShipmentDeadline: null,
        ResponseDeadline: null,
        Name: "",
        PickupAddress: "",
        DeliveryAddress: "",
        DeliverTo: "",
        MaxCarriers: null,
        MaxCarriersByLane: null,
        MaxCarriersInOrigin: null,
        MaxCarriersInDestination: null,
        marketIds: [],
        transportTypeIds: [],
        ContainerTypeId: 2,
        fclItems: [],
        lclMode: "volumes",
        lclBase: "plt",
        lclvolumes: { quantity: "", volumeM3: "", weightKg: "" },
        lclDimensions: [],
        carrierIds: [],
        tenderPhase: undefined,
      };
      setTenderData(reset); // apply clean draft
      dataRef.current = reset; // sync ref
      setActiveStep(0); // start at step 0
      markActive(); // mark active context
      setHydrated(true); // hydration complete
      setSession(ns, [
        { key: K.owner, value: String(tid) },
        { key: K.isCreatePhase, value: !!isCreatePhase },
        { key: K.tenderInfo, value: tenderInfo },
        { key: K.lastPhase, value: lastPhase },
        { key: K.phases, value: phases },
        { key: K.quotes, value: quotes },
        { key: K.lanes, value: lanes },
        { key: K.carriers, value: carriers },
        { key: K.first, value: true },
        { key: K.step, value: 0 },
        { key: K.data, value: reset },
        { key: K.gridMk, value: null },
        { key: K.gridTt, value: null },
        { key: K.gridCr, value: null },
        { key: K.carriersSel, value: [] },
      ]);
    };

    // Persist snapshot for previous tab before switching
    const prev = prevTabRef.current; // previously active tab
    if (prev !== null && prev !== selectedTab) {
      flushGridDebounces(); // flush any pending grid state
      persistSnapshot(prev);
    }
    // Hydrate new tab selection
    if (selectedTab) {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current); // cancel pending draft flush, if any
        persistDebounceRef.current = null;
      }
      lastDraftJsonRef.current = ""; // reset draft hash
      setHydrated(false); // mark as hydrating
      hydrateFromSession(stateKeyFrom(selectedTab), selectedTab); // restore
    }
    prevTabRef.current = selectedTab || null; // track current tab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, location.pathname]); // re-run when router path or tab changes

  //==================================================================================================================================================================
  // USEEFFECT:
  // Persist a minimal snapshot on unmount/refresh
  //==================================================================================================================================================================
  useEffect(() => {
    return () => {
      const tid = activeTidRef.current ?? prevTabRef.current ?? selectedTab; // best-effort resolve tab id
      const ns = stateKeyFrom(tid); // resolve namespace
      if (!ns) return; // guard
      flushGridDebounces();
      setSession(ns, [
        { key: K.owner, value: String(tid) },
        { key: K.step, value: stepRef.current },
        { key: K.data, value: dataRef.current },
        { key: K.gridMk, value: mkGridState },
        { key: K.gridTt, value: ttGridState },
        { key: K.gridCr, value: crGridState },
        { key: K.carriersSel, value: linkedCarriers },
        { key: K.shipperMkts, value: shipperMarkets },
      ]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // USEEFFECT:
  // Keep step and draft refs in sync with state
  //==================================================================================================================================================================
  useEffect(() => {
    stepRef.current = activeStep;
  }, [activeStep]); // mirror step into ref
  useEffect(() => {
    dataRef.current = tenderData;
  }, [tenderData]); // mirror draft into ref

  // Normalizer helper
  const norm = (d) => (Array.isArray(d?.Items) ? d.Items : Array.isArray(d) ? d : []);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Bootstrap main lookups in parallel
  //==================================================================================================================================================================
  useEffect(() => {
    let cancelled = false; // cancellation flag for async safety
    (async () => {
      incLoad();
      try {
        const [pkg, trans, tt] = await Promise.all([
          svcPackageTypes.get(),
          fetchAllTransportTypesPaged(),
          svcTenderTypes.get(),
        ]);
        if (!cancelled) {
          if (!isFailurePayload(pkg)) setPackageTypes(norm(pkg) || []); // only active
          if (Array.isArray(trans)) setTransportTypes(trans);
          if (!isFailurePayload(tt))
            setTenderTypeOptions(norm(tt).map((x) => ({ label: x.Name, value: x.Id })));
        }
      } catch (err) {
        if (!cancelled)
          toastRef.current("error", "", apiMessage(err, tRef.current("ErrorLoadingData"))); // notify
      } finally {
        decLoad();

        // Marks that the base lookups finished at least once (success or failure).
        if (!cancelled) setLookupsLoadedOnce(true);
      }
    })();
    return () => {
      cancelled = true;
    }; // cancel pending work on unmount/dep change
  }, [baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // USEEFFECT:
  // Load only the PHASE schema/options. DynamicForm now resolves FK lists internally,
  // so we no longer prefetch foreign tables here.
  //==================================================================================================================================================================
  useEffect(() => {
    let cancelled = false;

    (async () => {
      incLoad();
      try {
        const opts = await svcTenderPhases.options(); // schema/options for TenderPhases
        if (cancelled) return;

        if (isFailurePayload(opts)) {
          // Reset schema on error and surface a toast
          setPhaseSchemaOptions([]);
          toastRef.current("error", "", apiMessage(opts, tRef.current("ErrorLoadingData")));
          return;
        }

        // Normalize and store schema
        const schema = Array.isArray(opts) ? opts : [];
        setPhaseSchemaOptions(schema);
      } catch (err) {
        if (!cancelled) {
          setPhaseSchemaOptions([]);
          toastRef.current("error", "", apiMessage(err, tRef.current("ErrorLoadingData")));
        }
      } finally {
        decLoad();

        // Marks that the Phase schema finished at least once.
        if (!cancelled) setPhaseSchemaLoadedOnce(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [svcTenderPhases, i18n.language, baseUrl, incLoad, decLoad]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Load only the TENDER schema/options. DynamicForm now resolves FK lists internally,
  // so we no longer prefetch foreign tables here.
  //==================================================================================================================================================================
  useEffect(() => {
    let cancelled = false;

    (async () => {
      incLoad();
      try {
        const opts = await svcTenderOptions.options(); // schema/options for Tenders
        if (cancelled) return;

        if (isFailurePayload(opts)) {
          // Reset schema on error (no FK map anymore)
          setTenderSchemaOptions([]);
          return;
        }

        // Normalize and store schema
        const schema = Array.isArray(opts) ? opts : [];
        setTenderSchemaOptions(schema);
      } finally {
        decLoad();

        // Marks that the Tender schema finished at least once.
        if (!cancelled) setTenderSchemaLoadedOnce(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [svcTenderOptions, i18n.language, baseUrl, incLoad, decLoad]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Load full Tender DTO when creating a Phase; hydrate draft with read-only view data
  //==================================================================================================================================================================
  useEffect(() => {
    if (!isCreatePhase || !tenderInfo?.Id) return; // only when creating a phase with valid tender id
    let cancelled = false;

    (async () => {
      incLoad();
      try {
        // Ask for related entities to render read-only context
        const dto = await svcTenderGet.getId(
          `${tenderInfo.Id}?include=Phases,Markets,TransportTypes,FclPackages,LclPackage,Route`
        );
        if (isFailurePayload(dto) || cancelled) return;

        // Markets (ids + objects for view)
        const dtoMarketIds = (dto?.Markets || [])
          .map((m) => m?.Market?.Id)
          .filter(Boolean);
        const dtoMarketsObj = (dto?.Markets || []).map((m) => m?.Market).filter(Boolean);

        // Transport Types (ids + objects for view)
        const dtoTTIds = (dto?.TransportTypes || [])
          .map((tt) => tt?.TransportType?.Id)
          .filter(Boolean);
        const dtoTTObjs = transportTypes.filter((t) => dtoTTIds.includes(t.Id));

        // Patch draft with main fields
        let next = { ...dataRef.current };
        next = {
          ...next,
          TenderTypeId: Number(dto?.TenderTypeId ?? 1),
          routeId: Number(dto?.RouteId ?? 0) || null,
          message: dto?.Message ?? "",
          ShipperId: Number(dto?.ShipperId ?? 0) || null,
          ShipmentStartDate: dto?.ShipmentStartDate ?? null,
          ShipmentDeadline: dto?.ShipmentDeadline ?? null,
          ResponseDeadline: dto?.ResponseDeadline ?? null,
          Name: dto?.Name ?? "",
          PickupAddress: dto?.PickupAddress ?? "",
          DeliveryAddress: dto?.DeliveryAddress ?? "",
          DeliverTo: dto?.DeliverTo ?? "",
          MaxCarriers: dto?.MaxCarriers ?? null,
          MaxCarriersByLane: dto?.MaxCarriersByLane ?? null,
          MaxCarriersInOrigin: dto?.MaxCarriersInOrigin ?? null,
          MaxCarriersInDestination: dto?.MaxCarriersInDestination ?? null,
          ContainerTypeId: Number(dto?.ContainerTypeId ?? 1),
          marketIds: dtoMarketIds,
          transportTypeIds: dtoTTIds,
          // view-only extras
          Message: dto?.Message ?? "",
          Route: dto?.Route ?? "",
          Loads: typeof dto?.Loads === "number" ? dto.Loads : null,
          IsFinished: !!dto?.IsFinished,
          Inactive: !!dto?.Inactive,
        };

        // Map packages for view panels
        if (Number(dto?.ContainerTypeId) === 2) {
          // FCL
          const fcl = (dto?.FclPackages || []).map((p) => ({
            cargofiveEquipmentId: Number(p?.CargofiveEquipmentId ?? 0),
            quantity: Number(p?.Quantity ?? 0),
            weightKg: Number(p?.WeightKg ?? 0),
          }));
          next.fclItems = fcl;
          next.lclMode = "volumes";
          next.lclBase = "plt";
          next.lclvolumes = { quantity: "", volumeM3: "", weightKg: "" };
          next.lclDimensions = [];
        } else {
          // LCL
          const vol = dto?.LclPackage?.VolumePackage;
          const dim = dto?.LclPackage?.DimensionsPackage;
          if (vol) {
            next.ContainerTypeId = 1;
            next.lclMode = "volumes";
            next.lclBase = Number(vol?.PackageTypeId) === 2 ? "box" : "plt";
            next.lclvolumes = {
              quantity: Number(vol?.Quantity ?? 0),
              volumeM3: Number(vol?.VolumeM3 ?? 0),
              weightKg: Number(vol?.WeightKg ?? 0),
            };
            next.lclDimensions = [];
          } else if (dim) {
            next.ContainerTypeId = 1;
            next.lclMode = "dimensions";
            next.lclBase = Number(dim?.PackageTypeId) === 2 ? "box" : "plt";
            next.lclDimensions = [
              {
                quantity: Number(dim?.Quantity ?? 0),
                wCm: Number(dim?.WidthCm ?? 0),
                hCm: Number(dim?.HeightCm ?? 0),
                lCm: Number(dim?.LengthCm ?? 0),
                weightKg: Number(dim?.WeightKg ?? 0),
              },
            ];
            next.lclvolumes = { quantity: "", volumeM3: "", weightKg: "" };
          }
        }

        onDraft(next); // persist updated draft

        setPhaseMarkets(dtoMarketsObj); // set view-only markets list
        setPhaseTransportTypes(dtoTTObjs); // set view-only transport types list

        // Enrich tenderInfo for route preview
        const enrichedTenderInfo = {
          ...(tenderInfo || {}),
          Id: dto?.Id,
          RouteId: dto?.RouteId,
          marketIds: dtoMarketIds,
          transportTypeIds: dtoTTIds,
        };
        setSession(activeNsRef.current, [{ key: K.tenderInfo, value: enrichedTenderInfo }]);

        // Phases normalization (direct array, paged array, or fallback endpoint)
        let ph = [];
        if (Array.isArray(dto?.Phases)) {
          ph = dto.Phases;
        } else if (Array.isArray(dto?.Phases?.Items)) {
          ph = dto.Phases.Items;
        } else {
          try {
            const phasesSvc = new BufferService(`Tenders/${tenderInfo.Id}/Phases`, baseUrl);
            const pRes = await phasesSvc.get();
            if (!isFailurePayload(pRes)) {
              ph = Array.isArray(pRes?.Items)
                ? pRes.Items
                : Array.isArray(pRes)
                  ? pRes
                  : [];
            }
          } catch {
            /* best-effort */
          }
        }

        if (!cancelled) setDtoPhases(ph || []);

        // Determine last phase (prefer IsLastPhase; fallback by EndDate desc)
        const byFlag = (ph || []).find((p) => p?.IsLastPhase === true) || null;
        if (byFlag) {
          setSession(activeNsRef.current, [{ key: K.lastPhase, value: byFlag }]);
        } else if ((ph || []).length) {
          const lastByDate =
            ph
              .slice()
              .sort((a, b) => new Date(b?.EndDate || 0) - new Date(a?.EndDate || 0))[0] ||
            null;
          if (lastByDate)
            setSession(activeNsRef.current, [{ key: K.lastPhase, value: lastByDate }]);
        }

        setSession(activeNsRef.current, [{ key: K.phases, value: ph || [] }]); // store full phases
      } catch (err) {
        toastRef.current("error", "", apiMessage(err, tRef.current("ErrorLoadingData"))); // notify
      } finally {
        decLoad();
      }
    })();
  }, [isCreatePhase, tenderInfo?.Id, baseUrl, transportTypes, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // USEEFFECT:
  // Derive read-only phase markets/transport types from parent tender when creating a phase
  //==================================================================================================================================================================
  useEffect(() => {
    if (!isCreatePhase || !isFirstTime) return; // only at first render of "create phase" flow
    let cancelled = false;
    (async () => {
      incLoad();
      try {
        const ttIds = Array.isArray(tenderInfo?.transportTypeIds)
          ? tenderInfo.transportTypeIds
          : []; // ids or empty
        const tts = transportTypes.filter((tt) => ttIds.includes(tt.Id)); // map ids to objects
        if (!cancelled) setPhaseTransportTypes(tts);

        const mIds = Array.isArray(tenderInfo?.marketIds) ? tenderInfo.marketIds : []; // ids or empty
        if (mIds.length) {
          const details = await Promise.all(mIds.map((id) => svcMarkets.getId(id))); // fetch details
          const ok = details.filter((x) => !isFailurePayload(x)); // keep valid
          if (!cancelled) setPhaseMarkets(ok);
        } else {
          if (!cancelled) setPhaseMarkets([]); // none
        }
      } catch (err) {
        if (!cancelled)
          toastRef.current("error", "", apiMessage(err, tRef.current("ErrorLoadingPhaseData")));
      } finally {
        decLoad();
      }
    })();
    return () => {
      cancelled = true;
    }; // cancel pending work
  }, [isCreatePhase, isFirstTime, tenderInfo, transportTypes, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // BOOTSTRAP: Resolve ShipperId in strict order and disable the page if none is found
  // Order:
  //   1) localStorage.defaultShipperId (if present and not empty)
  //   2) User.DefaultAccountId -> Accounts/{id}.DefaultShipperId
  //   3) If still missing -> disable page + toast
  // Notes:
  //   - When a shipper is resolved, we attach it to the draft immediately.
  //==================================================================================================================================================================
  useEffect(() => {
    if (!hydrated) return; // only once after hydration
    if (Number(tenderData?.ShipperId) > 0) return; // already set
    if (!isFirstTime) return; // only at first render

    const LS = (k) => localStorage.getItem(k);
    const rawShipper = (LS("defaultShipperId") ?? "").trim();
    const userId = (LS("userId") ?? "").trim();

    const applyShipper = (shipperId) => {
      if (!shipperId) return false;
      onDraft({ ShipperId: Number(shipperId) });
      setPageDisabled(false);
      return true;
    };

    (async () => {
      incLoad();
      try {
        // 1) Try localStorage.defaultShipperId (treat empty string as null)
        if (rawShipper) {
          if (applyShipper(rawShipper)) return;
        }

        // 2) Try via User.DefaultAccountId -> Accounts/{id}.DefaultShipperId
        if (userId) {
          try {
            const userSvc = new BufferService(`Users/${userId}`, baseUrl);
            const user = await userSvc.get(); // { DefaultAccountId, ... }
            const accId = user?.DefaultAccountId ?? null;

            if (accId) {
              try {
                const accSvc = new BufferService(`Accounts/${accId}`, baseUrl);
                const acc = await accSvc.get(); // { DefaultShipperId, ... }
                const shu = acc?.DefaultShipperId ?? null;
                if (applyShipper(shu)) return;
              } catch {
                // Account fetch failed -> continue to failure
              }
            }
          } catch {
            // User fetch failed -> continue to failure
          }
        }

        // 3) All attempts failed → disable page and notify
        setPageDisabled(true);
        notifyDefaultShipperFailure();
      } finally {
        decLoad();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, tenderData?.ShipperId, baseUrl, isFirstTime]);

  //==================================================================================================================================================================
  // MARKETS LOAD: Load markets ONLY from the resolved Shipper (no fallback to ALL markets).
  // - In "create phase" we still show parent tender's markets read-only elsewhere, so we skip here.
  // - If no ShipperId is set, keep markets empty.
  // - Persist per-tab snapshot (K.shipperMkts) for tab switches.
  //==================================================================================================================================================================
  useEffect(() => {
    let cancelled = false;
    const sid = Number(tenderData?.ShipperId ?? 0) || 0;

    // helper to set markets state and persist in session
    const writeMarkets = (arr) => {
      setShipperMarkets(arr || []);
      const ns = activeNsRef.current;
      if (ns)
        setSession(ns, [{ key: K.shipperMkts, value: Array.isArray(arr) ? arr : [] }]);
    };

    (async () => {
      // In create phase, skip shipper markets load
      if (isCreatePhase) {
        writeMarkets([]);
        return;
      }

      // No Shipper defined sets markets to empty
      if (!sid) {
        writeMarkets([]);
        return;
      }

      incLoad();
      try {
        const svc = new BufferService(`Shippers/${sid}/Markets`, baseUrl);
        const res = await svc.get();
        if (cancelled) return;

        if (!isFailurePayload(res)) {
          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res)
              ? res
              : [];
          const onlyActive = (items || []).filter((x) => !x?.Inactive);
          writeMarkets(onlyActive);
        } else {
          // Error fetching shipper markets → empty (no global fallback)
          writeMarkets([]);
        }
      } catch {
        // Any error → empty (no global fallback)
        writeMarkets([]);
      } finally {
        if (!cancelled) decLoad();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenderData?.ShipperId, baseUrl, isCreatePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // Fetch invitable carriers on-demand (Step 3 only)
  // - This calls the endpoint: POST Tenders/InvitableCarriers
  // - We do NOT fetch all carriers and filter on the client anymore
  // - Uses Set to efficiently prune invalid selections after reload
  //==================================================================================================================================================================
  const fetchInvitableCarriers = useCallback(
    async ({ shipperId, marketIds, transportTypeIds, tenderId }) => {
      // Guard: without shipperId we cannot invite carriers in this flow
      if (!Number(shipperId)) return [];

      const body = {
        shipperId: Number(shipperId) || 0,
        marketIds: Array.isArray(marketIds) ? marketIds : [],
        transportTypeIds: Array.isArray(transportTypeIds) ? transportTypeIds : [],
        ...(Number(tenderId) > 0 ? { tenderId: Number(tenderId) } : {}),
      };

      // IMPORTANT:
      // - Your endpoint example is /api/Tenders/InvitableCarriers
      // - BufferService("Tenders/InvitableCarriers") should map correctly.
      const res = await svcInvitableCarriers.post(body);

      if (isFailurePayload(res)) return [];

      // Normalize response: can be { Items } or plain array
      const items = Array.isArray(res?.Items) ? res.Items : Array.isArray(res) ? res : [];

      // Defensive cleanup: remove inactive, normalize ids, stable sort
      const cleaned = (items || [])
        .filter((c) => !c?.Inactive)
        .map((c) => ({
          ...c,
          // Ensure we have a consistent Id field (SelectableTileGrid depends on it)
          Id: c?.Id ?? c?.CarrierId ?? c?.carrierId,
          Name: c?.Name ?? c?.CarrierName ?? c?.carrierName ?? "",
        }))
        .filter((c) => c?.Id != null);

      cleaned.sort((a, b) => String(a?.Name ?? "").localeCompare(String(b?.Name ?? "")));
      return cleaned;
    },
    [svcInvitableCarriers]
  );

  // Stable dependency keys for filters (avoids complex expressions in useEffect deps)
  const tenderMarketIdsKey = useMemo(() => {
    const ids = Array.isArray(tenderData?.marketIds) ? tenderData.marketIds : [];
    return ids.join(",");
  }, [tenderData?.marketIds]);

  const tenderTransportTypeIdsKey = useMemo(() => {
    const ids = Array.isArray(tenderData?.transportTypeIds) ? tenderData.transportTypeIds : [];
    return ids.join(",");
  }, [tenderData?.transportTypeIds]);

  //==================================================================================================================================================================
  // USEEFFECT:
  // Whenever we enter Step 3 (Carriers), fetch invitable carriers from the server using the current filters.
  // This replaces the old "load all + local filtering" approach.
  //==================================================================================================================================================================
  useEffect(() => {
    if (activeStep !== 2) return; // only on Carriers step

    let cancelled = false;

    (async () => {
      const d = dataRef.current || {};
      const shipperId = Number(d?.ShipperId ?? 0) || 0;

      // tenderId is only meaningful in create-phase flow (we already have tenderInfo.Id)
      // For create-tender flow, we send tenderId = 0 (as in your curl example).
      const tenderId = isCreatePhase ? Number(tenderInfo?.Id ?? 0) || 0 : 0;

      incLoad();
      try {
        const list = await fetchInvitableCarriers({
          shipperId,
          marketIds: Array.isArray(d?.marketIds) ? d.marketIds : [],
          transportTypeIds: Array.isArray(d?.transportTypeIds) ? d.transportTypeIds : [],
          tenderId,
        });

        if (cancelled) return;

        setAvailableCarriers(list);

        // Prune any previously selected carrier ids that are no longer invitable
        const validIds = new Set(list.map((c) => c.Id));
        setLinkedCarriers((prev) => (prev || []).filter((id) => validIds.has(id)));
      } catch {
        if (!cancelled) {
          setAvailableCarriers([]);
          setLinkedCarriers([]);
        }
      } finally {
        if (!cancelled) decLoad();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    isCreatePhase,
    tenderInfo?.Id,
    fetchInvitableCarriers,
    incLoad,
    decLoad,
    // Re-fetch when filters change while staying on step 3
    tenderMarketIdsKey,
    tenderTransportTypeIdsKey,
    tenderData.ShipperId,
  ]);

  // Current tender type id memo
  const currentTenderTypeId = useMemo(
    () => Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId ?? 1),
    [tenderData?.TenderTypeId, tenderData?.tenderTypeId]
  );

  // ======================================================================================================================================================
  // USEEFFECT: Sync selected carriers into the draft when they change
  //==================================================================================================================================================================
  useEffect(() => {
    if (!hydrated) return; // only after initial hydration
    const isSpot = currentTenderTypeId === 1; // spot or non-spot
    if (isCreatePhase) {
      onDraft({
        tenderPhase: {
          ...(tenderData.tenderPhase || {}),
          carrierIds: linkedCarriers,
        },
      }); // store on phase
    } else if (isSpot) {
      onDraft({ carrierIds: linkedCarriers }); // store on tender for spot
    }
  }, [linkedCarriers, hydrated, currentTenderTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // USEEFFECT:
  // Persist selected carriers into session snapshot when they change
  //==================================================================================================================================================================
  const carriersDebRef = useRef(null);
  useEffect(() => {
    const ns = activeNsRef.current;
    if (!ns) return;
    if (carriersDebRef.current) clearTimeout(carriersDebRef.current);
    carriersDebRef.current = setTimeout(() => {
      // evitar writes redundantes
      const snap = M_State.find((o) => o.key === ns)?.value || [];
      const prev = pick(snap, K.carriersSel);
      if (deepEq(prev, linkedCarriers)) return;
      setSession(ns, [{ key: K.carriersSel, value: linkedCarriers }]);
      carriersDebRef.current = null;
    }, 200);
    return () => {
      if (carriersDebRef.current) clearTimeout(carriersDebRef.current);
    };
  }, [linkedCarriers, M_State]); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // USEEFFECT:
  // Cleanup pending debounces on unmount
  //==================================================================================================================================================================
  useEffect(() => {
    const r = gridDebounceRef.current;
    return () => {
      if (!r) return;
      if (r.mk) clearTimeout(r.mk);
      if (r.tt) clearTimeout(r.tt);
      if (r.cr) clearTimeout(r.cr);
    };
  }, []);

  // Current selected Route id (phase uses read-only parent route)
  const routeIdResolved = useMemo(() => {
    return isCreatePhase ? tenderInfo?.RouteId ?? null : tenderData?.routeId ?? null;
  }, [isCreatePhase, tenderInfo?.RouteId, tenderData?.routeId]);

  // Guard default Route bootstrap so it runs ONLY once.
  // This prevents re-selecting the first Route after the user clears the selection.
  // We also purposely avoid including routeIdResolved in the effect deps below,
  // so clearing the value (-> null) won't trigger the bootstrap again.
  const routeBootstrapRef = useRef(false);

  useEffect(() => {
    if (isCreatePhase) return; // phase: route is read-only and comes from parent
    if (!hydrated) return; // wait for per-tab hydration before applying defaults
    if (routeBootstrapRef.current) return; // already bootstrapped once
    if (routeIdResolved != null) {
      // restored/persisted value -> respect it and mark bootstrapped
      routeBootstrapRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const order = encodeURIComponent("Description asc");
        const filter = encodeURIComponent("Inactive eq false");
        const first = await svcRoutes.filter(`?Page=1&PageSize=1&OrderBy=${order}&Filter=${filter}`);
        const items = Array.isArray(first?.Items) ? first.Items : Array.isArray(first) ? first : [];
        const id = items?.[0]?.Id ?? null;
        if (cancelled || id == null) return;
        // If hydration (or user selection) already provided a value, never override it.
        const currentRouteId = dataRef.current?.routeId ?? null;
        if (currentRouteId != null) return;
        onDraft({ routeId: id });
      } catch {
        /* noop */
      } finally {
        // Mark as bootstrapped so clearing the select later will NOT re-apply a default.
        routeBootstrapRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePhase, baseUrl, hydrated]);

  // ==================================================================================================
  // Refreshes lookup data (PackageTypes, TransportTypes, TenderTypes)
  // and (if currently on Step 3) re-fetches invitable carriers from the server.
  // ==================================================================================================
  const handleRefresh = async () => {
    incLoad();
    try {
      // 1) Refresh main lookups
      const [pkg, trans, tt] = await Promise.all([
        svcPackageTypes.get(),
        fetchAllTransportTypesPaged(),
        svcTenderTypes.get(),
      ]);

      if (!isFailurePayload(pkg)) setPackageTypes(norm(pkg) || []);
      if (Array.isArray(trans)) setTransportTypes(trans);
      if (!isFailurePayload(tt)) {
        setTenderTypeOptions(norm(tt).map((x) => ({ label: x.Name, value: x.Id })));
      }

      // 2) If we are in step 3, refresh carriers via the Inviendpoint
      if (stepRef.current === 2) {
        const d = dataRef.current || {};
        const tenderId = isCreatePhase ? Number(tenderInfo?.Id ?? 0) || 0 : 0;

        const list = await fetchInvitableCarriers({
          shipperId: Number(d?.ShipperId ?? 0) || 0,
          marketIds: Array.isArray(d?.marketIds) ? d.marketIds : [],
          transportTypeIds: Array.isArray(d?.transportTypeIds) ? d.transportTypeIds : [],
          tenderId,
        });

        setAvailableCarriers(list);

        // Drop any linked carriers that are no longer valid after recomputing
        const valid = new Set(list.map((c) => c.Id));
        setLinkedCarriers((prev) => (prev || []).filter((id) => valid.has(id)));
      }

      toastRef.current("success", "", tRef.current("Refreshed"));
    } catch (err) {
      toastRef.current("error", "", apiMessage(err, tRef.current("ErrorLoadingData")));
    } finally {
      decLoad();
    }
  };

  //==================================================================================================================================================================
  // Persists a partial draft with debounce and mirrors to session snapshot
  //==================================================================================================================================================================
  const onDraft = useCallback((partial) => {
    if (!partial || typeof partial !== "object") return; // guard: must be an object
    const merged = { ...dataRef.current, ...partial }; // build merged draft
    const json = (() => {
      try {
        return JSON.stringify(merged);
      } catch {
        return "";
      }
    })(); // safe JSON hash

    if (json === lastDraftJsonRef.current) return; // no change, skip
    lastDraftJsonRef.current = json; // update last hash
    dataRef.current = merged; // update ref

    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current); // cancel pending flush
    }
    // Debounce snapshot flush into session for smoother UX
    persistDebounceRef.current = setTimeout(() => {
      const ns = activeNsRef.current; // current namespace
      if (!ns) return; // guard
      setSession(ns, [
        { key: K.data, value: dataRef.current },
        { key: K.step, value: stepRef.current },
      ]);
    }, 250);

    setTenderData(merged); // update state
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //==================================================================================================================================================================
  // Builds the packages payload (FCL or LCL) from the current draft
  //==================================================================================================================================================================
  const buildPackagesPayload = (d) => {
    const containerTypeId = Number(d?.ContainerTypeId ?? 1); // 1=LCL, 2=FCL
    if (containerTypeId === 2) {
      // FCL packages
      const rows = Array.isArray(d?.fclItems) ? d.fclItems : [];
      const fclPackages = rows
        .slice(0, 3)
        .map((r) => ({
          cargofiveEquipmentId: Number(r?.cargofiveEquipmentId ?? 0),
          quantity: Number(r?.quantity ?? 0),
          weightKg: Number(r?.weightKg ?? 0),
        }))
        .filter((x) => x.cargofiveEquipmentId && x.quantity > 0); // keep valid rows
      return { fclPackages, lclPackage: null };
    }

    // LCL packages
    const mode = d?.lclMode === "dimensions" ? "dimensions" : "volumes";
    const baseIsBox = (d?.lclBase ?? "plt") === "box";
    const packageTypeId = baseIsBox ? 2 : 1; // 1=Pallet, 2=Box
    const lclPackage = { volumePackage: null, dimensionsPackage: null };

    if (mode === "volumes") {
      const v = d?.lclvolumes || {};
      lclPackage.volumePackage = {
        packageTypeId,
        quantity: Number(v?.quantity ?? 0),
        volumeM3: Number(v?.volumeM3 ?? 0),
        weightKg: Number(v?.weightKg ?? 0),
      };
    } else {
      const row = (Array.isArray(d?.lclDimensions) && d.lclDimensions[0]) || {};
      lclPackage.dimensionsPackage = {
        packageTypeId,
        quantity: Number(row?.quantity ?? 0),
        widthCm: Number(row?.wCm ?? row?.widthCm ?? 0),
        heightCm: Number(row?.hCm ?? row?.heightCm ?? 0),
        lengthCm: Number(row?.lCm ?? row?.lengthCm ?? 0),
        weightKg: Number(row?.weightKg ?? 0),
      };
    }
    return { fclPackages: [], lclPackage };
  };

  //==================================================================================================================================================================
  // Builds the tender payload from the current draft (spot determines carrierIds placement)
  //==================================================================================================================================================================
  const buildTenderPayload = (d, isSpot) => {
    const { fclPackages, lclPackage } = buildPackagesPayload(d); // derive package structures

    const toNullableInt = (value) => {
      if (value === null || value === undefined || value === "") return null;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    };

    return {
      name: d?.Name ?? "",
      shipmentStartDate: d?.ShipmentStartDate ?? null,
      shipmentDeadline: d?.ShipmentDeadline ?? null,
      responseDeadline: d?.ResponseDeadline ?? null,
      pickupAddress: d?.PickupAddress ?? "",
      deliveryAddress: d?.DeliveryAddress ?? "",
      deliverTo: d?.DeliverTo ?? "",
      message: d?.message ?? "",

      routeId: Number(d?.routeId ?? 0) || 0,
      containerTypeId: Number(d?.ContainerTypeId ?? 1),
      tenderTypeId: Number(d?.TenderTypeId ?? d?.tenderTypeId ?? 1),
      shipperId: Number(d?.ShipperId ?? 0) || null,

      maxCarriers: toNullableInt(d?.MaxCarriers),
      maxCarriersByLane: toNullableInt(d?.MaxCarriersByLane),
      maxCarriersInOrigin: toNullableInt(d?.MaxCarriersInOrigin),
      maxCarriersInDestination: toNullableInt(d?.MaxCarriersInDestination),

      fclPackages,
      lclPackage,

      marketIds: Array.isArray(d?.marketIds) ? d.marketIds : [],
      transportTypeIds: Array.isArray(d?.transportTypeIds) ? d.transportTypeIds : [],

      carrierIds: isSpot ? (Array.isArray(d?.carrierIds) ? d.carrierIds : []) : [],
    };
  };

  // Navigation helpers
  const goNext = (partial = {}) => {
    setTenderData((prev) => ({ ...prev, ...partial })); // patch draft before advancing
    if (activeStep < steps.length - 1) setActiveStep((s) => s + 1); // move to next step
  };
  const goBack = () => setActiveStep((s) => Math.max(0, s - 1)); // move to previous step

  //==================================================================================================================================================================
  // Validates package fields on step 0
  //==================================================================================================================================================================
  const validatePackagesForStep0 = () => {
    const ct = Number(tenderData?.ContainerTypeId ?? 1); // container type
    if (ct === 2) {
      // FCL requires 1..3 valid rows (equipment + quantity)
      const rows = Array.isArray(tenderData?.fclItems) ? tenderData.fclItems : [];
      const ok =
        rows.length >= 1 &&
        rows.length <= 3 &&
        rows.every((r) => Number(r?.cargofiveEquipmentId) > 0 && Number(r?.quantity) > 0);
      if (!ok) {
        toastRef.current("error", "", tRef.current("PleaseFillFCLPackages"));
        return false;
      }
      return true;
    } else {
      // LCL volumes or dimensions must be complete
      if ((tenderData?.lclMode ?? "volumes") === "volumes") {
        const v = tenderData?.lclvolumes || {};
        if (!(Number(v?.quantity) > 0)) {
          toastRef.current("error", "", tRef.current("PleaseFillLCLVolumes"));
          return false;
        }
        return true;
      } else {
        const d0 = Array.isArray(tenderData?.lclDimensions) && tenderData.lclDimensions[0];
        if (
          !(
            d0 &&
            Number(d0?.quantity) > 0 &&
            Number(d0?.wCm ?? d0?.widthCm) > 0 &&
            Number(d0?.hCm ?? d0?.heightCm) > 0 &&
            Number(d0?.lCm ?? d0?.lengthCm) > 0
          )
        ) {
          toastRef.current("error", "", tRef.current("PleaseFillLCLDimensions"));
          return false;
        }
        return true;
      }
    }
  };

  //==================================================================================================================================================================
  // Handles the "Next" action with per-step validation
  //==================================================================================================================================================================
  const handleNext = () => {
    if (activeStep === 0) {
      if (isCreatePhase) {
        const p = tenderData.tenderPhase; // current phase draft
        if (!p?.Name || !p?.StartDate || !p?.EndDate) {
          toastRef.current("error", "", tRef.current("PleaseFillRequiredPhaseFields"));
          return;
        }
        const hasMarket = Array.isArray(phaseMarkets) && phaseMarkets.length > 0;
        const hasTransportType =
          Array.isArray(phaseTransportTypes) && phaseTransportTypes.length > 0;
        if (!hasMarket) {
          toastRef.current("error", "", tRef.current("PleaseSelectAtLeastOneMarket"));
          return;
        }
        if (!hasTransportType) {
          toastRef.current("error", "", tRef.current("PleaseSelectAtLeastOneTransportType"));
          return;
        }
      } else {
        // Required tender fields
        const req = [
          Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId ?? 1),
          tenderData.ShipperId,
          tenderData.ShipmentStartDate,
          tenderData.ShipmentDeadline,
          tenderData.ResponseDeadline,
          tenderData.Name,
          tenderData.PickupAddress,
          tenderData.DeliveryAddress,
          tenderData.DeliverTo,
        ];
        if (req.some((x) => !x)) {
          toastMessages("error", "", t("PleaseFillRequiredTenderFields"));
          return;
        }
        if (!validatePackagesForStep0()) return;

        const hasMarket = Array.isArray(tenderData.marketIds) && tenderData.marketIds.length > 0;
        const hasTransportType =
          Array.isArray(tenderData.transportTypeIds) && tenderData.transportTypeIds.length > 0;
        if (!hasMarket) {
          toastMessages("error", "", t("PleaseSelectAtLeastOneMarket"));
          return;
        }
        if (!hasTransportType) {
          toastMessages("error", "", t("PleaseSelectAtLeastOneTransportType"));
          return;
        }

        const tenderTypeIdNow = Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId ?? 1);
        if (tenderTypeIdNow === 2) {
          // Non-spot requires a valid phase draft at this step
          const p = tenderData.tenderPhase || {};
          if (!p?.Name || !p?.StartDate || !p?.EndDate) {
            toastMessages("error", "", t("PleaseFillRequiredPhaseFields"));
            return;
          }
        }
      }
      goNext();
      return;
    }

    if (activeStep === 1) {
      if (!isCreatePhase && !tenderData.routeId) {
        // route is required when creating a tender
        toastMessages("error", "", t("PleaseSelectRoute"));
        return;
      }
      goNext();
      return;
    }

    if (activeStep === 2) {
      const isSpot = Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId ?? 1) === 1;
      const none = !linkedCarriers || linkedCarriers.length === 0; // require at least one carrier
      if ((isCreatePhase && none) || (!isCreatePhase && isSpot && none)) {
        toastMessages("error", "", t("PleaseSelectAtLeastOneCarrier"));
        return;
      }
      if (!isCreatePhase && !isSpot) {
        // Non-spot stores the selection in the phase draft
        const mergedPhase = { ...(tenderData.tenderPhase || {}), carrierIds: linkedCarriers };
        onDraft({ tenderPhase: mergedPhase });
      }
      goNext();
      return;
    }
  };

  //==================================================================================================================================================================
  // Submits the wizard: creates a tender and optional phase (or creates only a phase in create-phase flow)
  //==================================================================================================================================================================
  const handleSubmit = async () => {
    // Guard to prevent double-click / concurrent submissions
    if (isPostingRef.current) return; // prevent double click if already submitting
    isPostingRef.current = true; // lock (imperative flag)
    setIsPosting(true); // disable submit button in UI

    const currentTenderTypeId = Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId ?? 1);
    incLoad(); // increment global loading counter (turns spinner ON when crossing 0 -> 1)

    try {
      const isSpot = currentTenderTypeId === 1; // 1 = Spot, 2 = Non-Spot

      // Non-spot Tenders must have a valid Phase draft BEFORE we try to submit anything.
      // This validation is only for the "create tender" flow, not for "create phase" flow.
      if (!isCreatePhase && currentTenderTypeId === 2) {
        const p = tenderData.tenderPhase || {};
        if (!p?.Name || !p?.StartDate || !p?.EndDate) {
          // Missing mandatory fields on Phase draft → show error and abort submit
          toastMessages("error", "", t("PleaseFillRequiredPhaseFields"));
          return; // NOTE: finally{} will still execute → spinner and flags will be reset
        }
      }

      // CREATE-PHASE FLOW: we only create a Phase under an existing Tender (tenderInfo.Id)
      if (isCreatePhase) {
        // Create only a phase under an existing tender
        const resPhase = await svcPhaseCreate.post({
          tenderId: tenderInfo?.Id,
          ...(tenderData.tenderPhase || {}),
        });

        // If Phase creation fails, surface message and abort
        if (isFailurePayload(resPhase)) {
          toastMessages("error", "", apiMessage(resPhase, t("ErrorSaving")));
          return;
        }

        // Optional: update Tender message if user wrote one in the wizard
        if (tenderData.message) {
          const resMsg = await svcTenderUpdate.update(tenderInfo?.Id, {
            message: tenderData.message,
          });
          if (isFailurePayload(resMsg)) {
            toastMessages("error", "", apiMessage(resMsg, t("ErrorSaving")));
            return;
          }
        }

        // Optional: clear/normalize tab-scoped session snapshot after success
        // This avoids reopening with stale draft if user opens a new "Create Phase" tab later.
        const ns = activeNsRef.current;
        if (ns) {
          setSession(ns, [
            { key: K.data, value: null }, // clear stored draft
            { key: K.step, value: 0 }, // reset stored step
          ]);
          // If you prefer to fully wipe the namespace instead:
          // dispatch(M_StateActions.removeM_State({ key: ns }));
        }

        // Close the tab AFTER successful Phase creation
        handleTabClose(selectedTab); // close tab after success

        // Show success toast
        toastMessages("success", "", t("TenderPhaseCreated"));

        return;
      }

      // CREATE-TENDER FLOW (Spot and Non-Spot)
      if (isSpot) {
        // Spot: carriers live on Tender itself
        const payloadTender = buildTenderPayload(
          { ...tenderData, carrierIds: linkedCarriers }, // include selected carriers in payload
          true // isSpot = true
        );

        const resTender = await svcTenderCreate.post(payloadTender);

        if (isFailurePayload(resTender)) {
          // API returned an error -> show toast and abort
          toastMessages("error", "", apiMessage(resTender, t("ErrorSaving")));
          return;
        }
      } else {
        // Non-spot: Tender is created WITHOUT carriers, and the Phase carries the carrierIds

        // 1) Create Tender without carriers
        const payloadTender = buildTenderPayload(tenderData, false); // isSpot = false -> carrierIds = []
        const resTender = await svcTenderCreate.post(payloadTender);

        if (isFailurePayload(resTender)) {
          toastMessages("error", "", apiMessage(resTender, t("ErrorSaving")));
          return;
        }

        // Resolve created Tender Id from API response (defensive against different DTO shapes)
        const createdTenderId = resTender?.Id ?? resTender?.id ?? resTender?.TenderId ?? null;

        if (!createdTenderId) {
          // Unexpected response shape (no Id) -> we cannot create the Phase
          toastMessages("error", "", t("UnexpectedResponse"));
          return;
        }

        // 2) Create Phase with carriers, attached to the created Tender
        const phasePayload = {
          ...(tenderData.tenderPhase || {}),
          carrierIds: linkedCarriers || [],
          tenderId: createdTenderId,
        };

        const resPhase = await svcPhaseCreate.post(phasePayload);

        if (isFailurePayload(resPhase)) {
          toastMessages("error", "", apiMessage(resPhase, t("ErrorSaving")));
          return;
        }
      }

      // Optional: clear/normalize tab-scoped session snapshot after successful Tender + Phase flow
      const ns = activeNsRef.current;
      if (ns) {
        setSession(ns, [
          { key: K.data, value: null }, // clear stored draft
          { key: K.step, value: 0 }, // reset stored step
        ]);
      }

      // Close the tab AFTER successful Tender creation (Spot or Non-Spot)
      handleTabClose(selectedTab); // close tab after success

      // Show success toast
      toastMessages("success", "", t("TenderCreated"));
      return;
    } catch (err) {
      // Any unexpected error during the submit flow → show a generic error toast
      toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
    } finally {
      // Always executed (success, error, or early return inside try):
      // - Decrement global loading counter (turns spinner OFF when going 1 -> 0)
      // - Reset posting flags so the user can submit again if needed
      decLoad();
      setIsPosting(false);
      isPostingRef.current = false;
    }
  };

  // =================================================================================================
  // TABLE (TenderCarrierDetailView) DATA SOURCES
  // - Table shows ONLY for Non-Spot and for any selected phase (including first phase).
  // - Default target phase is the most recent eligible phase.
  // - Lanes: TenderLanes/flat filtered by SatisfiedInTenderPhaseId
  // - Quotes: TenderQuotes filtered by TenderPhaseId
  // - Carriers (select box): TenderPhaseCarriers/carrier filtered by TenderPhaseId (invited carriers)
  // =================================================================================================
  const [tablePhaseId, setTablePhaseId] = useState(null);
  const [tableLanes, setTableLanes] = useState([]);
  const [tableQuotes, setTableQuotes] = useState([]);
  const [tableInvitedCarriers, setTableInvitedCarriers] = useState([]);

  const fetchAllPaged = useCallback(async (svc, pageSize = 250, qs = "") => {
    let page = 1;
    const all = [];
    while (true) {
      const res = await svc.filter(`?Page=${page}&PageSize=${pageSize}${qs || ""}`);
      if (isFailurePayload(res)) break;

      const items = Array.isArray(res?.Items) ? res.Items : Array.isArray(res) ? res : [];
      if (!items?.length) break;

      all.push(...items);

      const totalPages = Number(res?.TotalPages ?? 1);
      if (page >= totalPages) break;
      page += 1;
    }
    return all;
  }, []);

  // Pick default target phase (most recent) when entering step 3 (Carriers)
  useEffect(() => {
    if (activeStep !== 2) return;

    const isSpot = currentTenderTypeId === 1;
    if (isSpot) {
      setTablePhaseId(null);
      return;
    }

    const phasesForView =
      Array.isArray(dtoPhases) && dtoPhases.length
        ? dtoPhases
        : Array.isArray(phases)
          ? phases
          : [];

    // Include ALL phases (first phase included)
    if (!phasesForView.length) {
      setTablePhaseId(null);
      return;
    }

    // Prefer IsLastPhase; fallback by EndDate desc
    const byFlag = phasesForView.find((p) => p?.IsLastPhase === true) || null;
    const byDate =
      phasesForView
        .slice()
        .sort((a, b) => new Date(b?.EndDate || 0) - new Date(a?.EndDate || 0))[0] || null;

    const nextId = (byFlag ?? byDate)?.Id ?? null;

    // Only set default if not already chosen
    setTablePhaseId((prev) => (prev == null ? nextId : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, currentTenderTypeId, JSON.stringify(dtoPhases || phases)]);

  // Load table datasets for the selected target phase
  useEffect(() => {
    const tenderId = Number(tenderInfo?.Id ?? tenderInfo?.id ?? 0) || 0;
    if (activeStep !== 2) return;

    const isSpot = currentTenderTypeId === 1;
    if (isSpot) {
      setTableLanes([]);
      setTableQuotes([]);
      setTableInvitedCarriers([]);
      return;
    }

    const phaseId = Number(tablePhaseId ?? 0) || 0;
    if (!phaseId || !tenderId) {
      setTableLanes([]);
      setTableQuotes([]);
      setTableInvitedCarriers([]);
      return;
    }

    let cancelled = false;

    const svcTenderLanesFlat = new BufferService("TenderLanes/flat", baseUrl);
    const svcTenderQuotes = new BufferService("TenderQuotes", baseUrl);
    const svcTenderPhaseCarriers = new BufferService("TenderPhaseCarriers/carrier", baseUrl);

    (async () => {
      incLoad();
      try {
        // 1) Lanes (flat) filtered by target phase:
        //    satisfiedInTenderPhaseId eq {target tenderphaseID}
        const lanesQs = `&Filter=${encodeURIComponent(
          `TenderId eq ${tenderId} and SatisfiedInTenderPhaseId eq ${phaseId}`
        )}`;

        const lanesFiltered = await fetchAllPaged(svcTenderLanesFlat, 250, lanesQs);
        if (cancelled) return;

        // NOTE: TenderLanes/flat returns: laneName, sourceLocation, destinationLocation
        const lanesNormalized = (lanesFiltered || []).map((l) => {
          const laneName =
            l?.laneName ??
            l?.LaneName ??
            l?.Name ??
            l?.name ??
            l?.Description ??
            l?.description ??
            "";

          const origin =
            l?.sourceLocation ??
            l?.SourceLocation ??
            l?.OriginName ??
            l?.originName ??
            l?.Origin?.Name ??
            l?.origin?.name ??
            l?.Source?.Name ??
            l?.source?.name ??
            "";

          const dest =
            l?.destinationLocation ??
            l?.DestinationLocation ??
            l?.DestinationName ??
            l?.destinationName ??
            l?.Destination?.Name ??
            l?.destination?.name ??
            l?.Target?.Name ??
            l?.target?.name ??
            "";

          const fallback =
            origin || dest ? `${String(origin || "").trim()} → ${String(dest || "").trim()}` : "";

          const finalName = String(laneName || fallback || "").trim();

          return {
            ...l,

            // Name (for any component that just prints "Name" / "LaneName")
            Name: finalName,
            LaneName: finalName,
            laneName: finalName,

            // Origin / Destination (cover camel + Pascal + alt field names)
            OriginName: String(origin || "").trim(),
            originName: String(origin || "").trim(),
            DestinationName: String(dest || "").trim(),
            destinationName: String(dest || "").trim(),

            // Also mirror into the most common "location" props used by tables
            SourceLocation: String(origin || "").trim(),
            sourceLocation: String(origin || "").trim(),
            DestinationLocation: String(dest || "").trim(),
            destinationLocation: String(dest || "").trim(),
          };
        });

        // 2) Quotes filtered by selected phase:
        //    tenderPhaseId eq {selected tenderphaseId}
        const quotesQs = `&Filter=${encodeURIComponent(
          `TenderId eq ${tenderId} and TenderPhaseId eq ${phaseId}`
        )}`;

        const quotesFiltered = await fetchAllPaged(svcTenderQuotes, 250, quotesQs);
        if (cancelled) return;

        // 3) Carriers invited for the selected phase:
        //    TenderPhaseCarriers/carrier filtered by tenderPhaseId
        const carriersQs = `&Filter=${encodeURIComponent(
          `TenderId eq ${tenderId} and TenderPhaseId eq ${phaseId}`
        )}`;

        const allPhaseCarriers = await fetchAllPaged(svcTenderPhaseCarriers, 250, carriersQs);
        if (cancelled) return;

        const invited = (allPhaseCarriers || [])
          .map((pc) => {
            const carrierId = pc?.CarrierId ?? pc?.carrierId ?? null;

            // IMPORTANT: some entries may lack Carrier info (deleted carriers)
            const carrierName = pc?.Carrier ?? pc?.carrier ?? "";

            return carrierId != null
              ? { Id: Number(carrierId), Name: String(carrierName || "") }
              : null;
          })
          .filter(Boolean)
          .filter((c, idx, arr) => arr.findIndex((x) => x.Id === c.Id) === idx)
          .sort((a, b) => String(a?.Name ?? "").localeCompare(String(b?.Name ?? "")));

        if (cancelled) return;

        setTableLanes(lanesNormalized);
        setTableQuotes(quotesFiltered);
        setTableInvitedCarriers(invited);
      } catch {
        if (!cancelled) {
          setTableLanes([]);
          setTableQuotes([]);
          setTableInvitedCarriers([]);
        }
      } finally {
        if (!cancelled) decLoad();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    currentTenderTypeId,
    tablePhaseId,
    baseUrl,
    tenderInfo?.Id,
    tenderInfo?.id,
    fetchAllPaged,
    incLoad,
    decLoad,
  ]);

  // Stable dependency for carrier ids (avoids complex expressions in useEffect deps)
  const tableInvitedCarrierIdsKey = useMemo(() => {
    const ids = (tableInvitedCarriers || []).map((c) => c.Id).filter((x) => x != null);
    ids.sort((a, b) => Number(a) - Number(b));
    return ids.join(",");
  }, [tableInvitedCarriers]);

  // Eligible phases for the table (Non-Spot) — include ALSO the first phase
  const tableEligiblePhases = useMemo(() => {
    const phasesForView =
      Array.isArray(dtoPhases) && dtoPhases.length
        ? dtoPhases
        : Array.isArray(phases)
          ? phases
          : [];

    // Include all phases (first phase included)
    return (phasesForView || [])
      .slice()
      .sort((a, b) => new Date(b?.EndDate || 0) - new Date(a?.EndDate || 0));
  }, [dtoPhases, phases]);

  const selectedTablePhase = useMemo(() => {
    const pid = Number(tablePhaseId ?? 0) || 0;
    if (!pid) return null;
    return (tableEligiblePhases || []).find((p) => Number(p?.Id ?? 0) === pid) || null;
  }, [tablePhaseId, tableEligiblePhases]);

  const shouldShowCarrierResponsesTable = useMemo(() => {
    const isSpot = currentTenderTypeId === 1;
    if (isSpot) return false;
    // Show for any selected phase (including the first)
    return !!selectedTablePhase?.Id;
  }, [currentTenderTypeId, selectedTablePhase]);

  // Default selected carrier (center) from invited carriers list
  const tableMidCarrierId = useMemo(() => {
    const arr = Array.isArray(tableInvitedCarriers) ? tableInvitedCarriers : [];
    const id = arr.length ? arr[Math.floor(arr.length / 2)]?.Id ?? null : null;
    return id;
  }, [tableInvitedCarriers]);

  const [tableCarrierId, setTableCarrierId] = useState(null);

  // Keep selected carrier stable; default it when phase or carriers change
  useEffect(() => {
    if (!shouldShowCarrierResponsesTable) {
      setTableCarrierId(null);
      return;
    }
    setTableCarrierId((prev) => {
      const validIds = new Set((tableInvitedCarriers || []).map((c) => c.Id));
      if (prev != null && validIds.has(prev)) return prev;
      return tableMidCarrierId ?? null;
    });
  }, [
    shouldShowCarrierResponsesTable,
    tableMidCarrierId,
    tableInvitedCarrierIdsKey,
    tableInvitedCarriers,
  ]);

  // =================================================================================================
  // Extra selects for Tender form (tender type + container type)
  // =================================================================================================
  const extraSelectsForTender = useMemo(() => {
    const obj = {
      TenderTypeId: tenderTypeOptions || [],
      ContainerTypeId: [
        { label: "LCL", value: 1 },
        { label: "FCL", value: 2 },
      ],
    };
    return obj;
  }, [tenderTypeOptions]);

  // Memoized refs for PackagesPanel
  const memoFclItems = useMemo(() => tenderData.fclItems || [], [tenderData.fclItems]);
  const memoLclVolumes = useMemo(
    () => tenderData.lclvolumes || { quantity: "", volumeM3: "", weightKg: "" },
    [tenderData.lclvolumes]
  );
  const memoLclDimensions = useMemo(() => tenderData.lclDimensions || [], [tenderData.lclDimensions]);

  // Small setters for PackagesPanel (inline commented)
  const setFclItemsCb = useCallback((items) => onDraft({ fclItems: items }), [onDraft]);
  const setLclModeCb = useCallback((m) => onDraft({ lclMode: m }), [onDraft]);
  const setLclBaseCb = useCallback((b) => onDraft({ lclBase: b }), [onDraft]);
  const setLclVolumesCb = useCallback((v) => onDraft({ lclvolumes: v }), [onDraft]);
  const setLclDimensionsCb = useCallback((rows) => onDraft({ lclDimensions: rows }), [onDraft]);

  //==================================================================================================================================================================
  // Renders step 1: Tender main info + (optional) last phase view + new phase editor + markets/TT + packages
  //==================================================================================================================================================================
  const renderStep1 = () => {
    const containerTypeId = Number(tenderData?.ContainerTypeId ?? 1); // derive package panel mode

    // Prefer phases from loaded DTO; fallback to router phases
    const phasesArr =
      Array.isArray(dtoPhases) && dtoPhases.length
        ? dtoPhases
        : Array.isArray(phases)
          ? phases
          : [];

    // Last phase: prefer IsLastPhase; fallback by EndDate desc
    const lastPhaseData = isCreatePhase
      ? lastPhase ??
      phasesArr.find((p) => p?.IsLastPhase === true) ??
      (phasesArr.length
        ? phasesArr
          .slice()
          .sort((a, b) => new Date(b?.EndDate || 0) - new Date(a?.EndDate || 0))[0]
        : null)
      : null;

    return (
      <div>
        {/* 1) Tender information (always visible) */}
        <div className="divFormCreateTender divFormCreateTender--padded">
          <DynamicForm
            key={`df-tender-${hydrateKey}-${isCreatePhase ? "view" : "create"}`}
            mode={isCreatePhase ? "view" : "create"}
            schema={tenderSchemaOptions}
            data={tenderData}
            onSubmit={() => { }}
            onValuesChange={onDraft}
            extraSelects={extraSelectsForTender}
            includeInCreate={includeInCreateTender} // Overrides create-specific fields
            title={t("Tender Information")}
          />
        </div>

        {/* 2) Last Tender Phase Information (create-phase mode only, if available) */}
        {isCreatePhase && lastPhaseData && (
          <div className="divFormCreateTender divFormCreateTender--padded">
            <DynamicForm
              key={`df-lastphase-${hydrateKey}`}
              mode="view"
              schema={phaseSchemaOptions}
              data={lastPhaseData}
              onSubmit={() => { }}
              onValuesChange={null} // view only
              extraSelects={{}}
              title={t("LastTenderPhase")}
            />
          </div>
        )}

        {/* 3) New Tender Phase (create-phase) OR Non-Spot phase in step 1 (create tender non-spot) */}
        {(isCreatePhase ||
          Number(tenderData?.TenderTypeId ?? tenderData?.tenderTypeId) === 2) && (
            <div className="divFormCreateTender divFormCreateTender--padded divFormCreateTender--alignInputs">
              <DynamicForm
                key={`df-newphase-${hydrateKey}`}
                mode="create"
                schema={phaseSchemaOptions}
                data={tenderData.tenderPhase || {}}
                onSubmit={() => { }}
                onValuesChange={(patch) => {
                  onDraft({
                    tenderPhase: { ...(tenderData.tenderPhase || {}), ...patch },
                  });
                }}
                extraSelects={{}}
                includeInCreate={includeInCreatePhase} // overrides create-specific fields
                title={t("NewTenderPhase")}
                containerClassName="alignInputsUserForm"
              />
            </div>
          )}

        {/* Markets + TransportTypes */}
        <div className="divAlignShipperMarkets">
          <div className="divFormCreateTender">
            {/* Markets */}
            {hydrated && activeNsRef.current && (
              <SelectableTileGrid
                key={`mk-${tabScopeId}-${hydrateKey}`}
                pageSize={20}
                stateScopeId={`${tabScopeId}::grid::markets`}
                initialState={mkGridState || undefined}
                initialOrderDir={mkGridState?.orderDir === "desc" ? "desc" : "asc"}
                onStateChange={(st) => {
                  setMkGridState((prev) => (deepEq(prev, st) ? prev : st));
                  const ns = activeNsRef.current;
                  persistGridState(ns, K.gridMk, st, "mk");
                }}
                title={t("Markets")}
                // View mode when creating a phase: just shows the tender's markets
                viewMode={!!isCreatePhase}
                items={isCreatePhase ? phaseMarkets : undefined}
                // When not in view-mode, we use the general endpoint (Page/Order/Filter server-side)
                endpoint="Markets"
                idField="Id"
                displayField="Name"
                queryable={!isCreatePhase}
                orderable={!isCreatePhase}
                staticFilter="Inactive eq false"
                selectableIds={selectableMarketIds}
                selectedIds={
                  isCreatePhase
                    ? (phaseMarkets || []).map((m) => m.Id)
                    : tenderData.marketIds || []
                }
                minHeight={"500px"}
                maxHeight={"500px"}
                onItemToggle={(item, next) => {
                  if (isCreatePhase) return;
                  const id = item?.Id ?? item?.id;
                  const prev = tenderData.marketIds || [];
                  onDraft({
                    marketIds: next ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
                  });
                }}
              />
            )}
          </div>

          {/* Transport Types */}
          <div className="divFormCreateTender">
            {hydrated && activeNsRef.current && (
              <SelectableTileGrid
                key={`tt-${tabScopeId}-${hydrateKey}`}
                pageSize={10}
                stateScopeId={`${tabScopeId}::grid::transportTypes`}
                initialState={ttGridState || undefined}
                initialOrderDir={ttGridState?.orderDir === "desc" ? "desc" : "asc"}
                onStateChange={(st) => {
                  setTtGridState((prev) => (deepEq(prev, st) ? prev : st));
                  const ns = activeNsRef.current;
                  persistGridState(ns, K.gridTt, st, "tt");
                }}
                title={t("TransportTypes")}
                viewMode={!!isCreatePhase}
                items={isCreatePhase ? phaseTransportTypes : undefined}
                endpoint="TransportTypes"
                queryable={!isCreatePhase}
                orderable={!isCreatePhase}
                idField="Id"
                displayField="Name"
                staticFilter="Inactive eq false"
                selectedIds={
                  isCreatePhase
                    ? (phaseTransportTypes || []).map((tt) => tt.Id)
                    : tenderData.transportTypeIds || []
                }
                minHeight={"500px"}
                maxHeight={"500px"}
                onItemToggle={(item, next) => {
                  if (isCreatePhase) return;
                  const id = item?.Id ?? item?.id;
                  const prev = tenderData.transportTypeIds || [];
                  onDraft({
                    transportTypeIds: next ? [...new Set([...prev, id])] : prev.filter((x) => x !== id),
                  });
                }}
              />
            )}
          </div>
        </div>

        {/* Packages */}
        <div className="divFormCreateTender divFormCreateTender--padded">
          <PackagesPanel
            containerTypeId={containerTypeId}
            packageTypes={packageTypes}
            fclItems={memoFclItems}
            setFclItems={setFclItemsCb}
            lclMode={tenderData.lclMode || "volumes"}
            setLclMode={setLclModeCb}
            lclBase={tenderData.lclBase || "plt"}
            setLclBase={setLclBaseCb}
            lclvolumes={memoLclVolumes}
            setLclvolumes={setLclVolumesCb}
            lclDimensions={memoLclDimensions}
            setLclDimensions={setLclDimensionsCb}
            readOnly={!!isCreatePhase}
          />
        </div>
      </div>
    );
  };

  //==================================================================================================================================================================
  // Renders step 2: Route selection + preview
  //==================================================================================================================================================================
  const renderStep2 = () => {
    return (
      <div className="ct-stepBox">
        <span className="ct-sectionTitle">{t("SelectRoute")}</span>

        <div className="ct-routeSelectWrapper">
          <DynamicSelectBox
            label={t("Route")}
            tableName="Routes"
            groupBy="Description"
            valueId={routeIdResolved}
            onChange={(opt) => onDraft({ routeId: opt?.value ?? null })}
            required
            autocomplete
            disabled={isCreatePhase}
            pageSize={20}
          />
        </div>

        {routeIdResolved && (
          <div className="ct-routePreviewWrapper">
            <span className="ct-sectionSubtitle">{t("RoutePreview")}</span>
            <RoutePreviewTable routeId={routeIdResolved} />
          </div>
        )}
      </div>
    );
  };

  //==================================================================================================================================================================
  // Renders step 3: Carriers selection list + (optional) non-spot carrier responses table
  //==================================================================================================================================================================
  const renderStep3 = () => {

    // Prefer phases from loaded DTO; fallback to router phases
    const phasesForView =
      Array.isArray(dtoPhases) && dtoPhases.length
        ? dtoPhases
        : Array.isArray(phases)
          ? phases
          : [];

    return (
      <div>
        <div className="divFormCreateTender divFormCreateTender--padded ct-step3Container">
          {/* Invitation grid (now uses server-filtered items, not endpoint="Carriers") */}
          {hydrated && activeNsRef.current && (
            <SelectableTileGrid
              key={`cr-${tabScopeId}-${hydrateKey}`}
              pageSize={50}
              stateScopeId={`${tabScopeId}::grid::carriers`}
              initialState={crGridState || undefined}
              initialOrderDir={crGridState?.orderDir === "desc" ? "desc" : "asc"}
              onStateChange={(st) => {
                setCrGridState((prev) => (deepEq(prev, st) ? prev : st));
                const ns = activeNsRef.current;
                persistGridState(ns, K.gridCr, st, "cr");
              }}
              title={t("CarriersInvitation")}
              // IMPORTANT: viewMode + items means we do NOT load all carriers from /Carriers anymore.
              // The server already decides which carriers are invitable.
              viewMode={false}
              items={availableCarriers}
              endpoint="Carriers" // kept for backward compatibility, but unused in viewMode
              idField="Id"
              displayField="Name"
              queryable={true}
              orderable={true}
              staticFilter="Inactive eq false"
              selectableIds={selectableCarrierIds}
              selectedIds={linkedCarriers}
              minHeight={"500px"}
              maxHeight={"700px"}
              onItemToggle={(item, next) => {
                const id = item?.Id ?? item?.id;
                setLinkedCarriers((prev) =>
                  next ? [...new Set([...(prev || []), id])] : (prev || []).filter((x) => x !== id)
                );
              }}
            />
          )}

          {/* Carrier responses table (ONLY Non-Spot and only when a phase is selected) */}
          {shouldShowCarrierResponsesTable && (
            <div className="ct-step3ResponsesWrapper">
              <span className="ct-sectionTitle">{t("CarriersResponsesFromPreviousPhases")}</span>

              <TenderCarrierDetailView
                tenderInfo={{
                  ...(tenderInfo || {}),
                  tenderTypeId: 2, // Non-Spot
                }}
                phases={phasesForView}
                quotes={tableQuotes}
                carriers={tableInvitedCarriers}
                lanes={tableLanes}
                isSpotTender={false}
                isRefreshable={false}
                onRefresh={() => { }}
                selectedPhaseId={tablePhaseId} // current selected phase
                setSelectedPhaseId={setTablePhaseId} // when changing phase,re-load data
                selectedCarrierId={tableCarrierId}
                key={`tcdv-${hydrateKey}-${tablePhaseId || "none"}`}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  //==================================================================================================================================================================
  // Renders step 4: Final message input
  //==================================================================================================================================================================
  const renderStep4 = () => {
    const MAX_LEN = 2048; // max message length
    return (
      <div className="ct-stepBox">
        <span className="ct-sectionTitle">{t("SubmitMessage")}</span>

        <MTextArea
          label={t("Message")}
          value={tenderData.message || ""}
          onChange={(e) => {
            const value = (e?.target?.value ?? "").slice(0, MAX_LEN); // clamp to max len
            onDraft({ message: value }); // persist message draft
          }}
          rows={6}
        />
        <div className="ct-charCounter">
          {(tenderData.message || "").length} / {MAX_LEN}
        </div>
      </div>
    );
  };

  //==================================================================================================================================================================
  // Main render
  //==================================================================================================================================================================
  return (
    <div className="createTenderPageRoot">
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("RefreshTable")}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={handleRefresh}
              label={t("RefreshTable")}
            />
            {activeStep === 3 && (
              <MButton
                backgroundColor="white"
                labelColor="var(--color-black)"
                icon={<i className="pi pi-save" style={{ color: "var(--color-green)" }}></i>}
                label={t("Send")}
                onClick={handleSubmit}
                disabled={pageDisabled || isPosting}
              />
            )}

          </>
        }
      />

      {pageDisabled && (
        <div className="ct-pageDisabledAlert">
          <b>{t("FailDetectDefaultShipper")}</b>
          <div className="ct-pageDisabledSubtext">{t("PleaseSetDefaultShipper")}</div>
        </div>
      )}

      <div className="divGlobalFormCreateTender">
        <div className="titleTab">
          <img
            src={
              activeStep === 0
                ? "/icons/svgsDaNet/layout-grid-add.svg"
                : activeStep === 1
                  ? "/icons/svgsDaNet/route.svg"
                  : activeStep === 2
                    ? "/icons/svgsDaNet/truck.svg"
                    : activeStep === 3
                      ? "/icons/svgsDaNet/message-2.svg"
                      : "/icons/svgsDaNet/crane.svg"
            }
            alt="icon"
            className="titleTabIcon"
          />
          {t(steps[activeStep])}
        </div>

        <Stepper
          alternativeLabel={matchesAlternative && !matches}
          orientation={matches ? "vertical" : "horizontal"}
          activeStep={activeStep}
          className="createTenderStepper"
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>
                <b>{t(label)}</b>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <div>
          <div className="divButtonsBackNext">
            <div className="divButtonBack">
              <MButton
                disabled={activeStep === 0}
                onClick={goBack}
                label={t("Back")}
                icon={<i className="pi pi-arrow-left iconTable"></i>}
                borderColor={"var(--color-grey)"}
              />
            </div>
            <div className="divButtonNext">
              <MButton
                onClick={handleNext}
                disabled={pageDisabled || activeStep === steps.length - 1}
                label={t("Next")}
                icon={<i className="pi pi-arrow-right iconTable"></i>}
                borderColor={"var(--color-grey)"}
                iconPosition={"right"}
              />
            </div>
          </div>

          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}
          {activeStep === 3 && renderStep4()}
        </div>
      </div>
    </div>
  );
}
